// frontend/src/pages/SearchPage.js
import React, { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import {
  Search,
  ArrowLeft,
  Play,
  Clock,
  List,
  Folder,
  Lock,
  Newspaper,
  HelpCircle,
  Trophy,
  LayoutGrid,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/apiService";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ContractBadge from "../components/common/ContractBadge";
import LanguageBadge from "../components/common/LanguageBadge";

const SearchResultCard = ({ item, type }) => {
  const { t } = useLanguage();
  // Use the hasAccess value calculated by the backend
  const canAccess = item.hasAccess;

  const getIcon = () => {
    switch (type) {
      case "category":
        return <Folder className="w-5 h-5 text-[#00A3E0]" />;
      case "video":
        return <Play className="w-5 h-5 text-[#00A3E0]" />;
      case "playlist":
        return <List className="w-5 h-5 text-[#00A3E0]" />;
      case "article":
        return <Newspaper className="w-5 h-5 text-[#00A3E0]" />;
      case "quiz":
        return <HelpCircle className="w-5 h-5 text-[#00A3E0]" />;
      default:
        return null;
    }
  };

  const getImageUrl = () => {
    switch (type) {
      case "category":
        return item.imageUrl;
      case "video":
      case "playlist":
        return item.thumbnailUrl;
      case "quiz":
        return item.imageUrl;
      case "article":
        // Articles have full URLs already
        return item.imageUrl?.startsWith('http')
          ? item.imageUrl
          : `https://www.burnair.cloud/images/news/${item.imageUrl}`;
      default:
        return null;
    }
  };

  const getLink = () => {
    // For articles with URL type, return the external URL
    if (type === "article" && item.news_type === "url" && item.urlDE) {
      return item.urlDE;
    }
    return `/${type}/${item.slug}`;
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const isExternalLink = type === "article" && item.news_type === "url" && item.urlDE;

  return (
    <Link
      to={canAccess ? getLink() : "#"}
      target={isExternalLink ? "_blank" : undefined}
      rel={isExternalLink ? "noopener noreferrer" : undefined}
      className={`group bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-200 border border-gray-200 ${
        canAccess
          ? "hover:scale-105 cursor-pointer hover:shadow-lg"
          : "opacity-60 cursor-not-allowed pointer-events-none"
      }`}
    >
      <div className="aspect-video bg-gray-200 relative">
        <img
          src={getImageUrl()}
          alt={t(item.title)}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />

        {!canAccess && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <Lock className="w-8 h-8 text-white" />
          </div>
        )}

        <div className="absolute top-2 left-2 flex items-center space-x-1 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
          {getIcon()}
          <span className="capitalize">
            {t({
              en: type,
              de:
                type === "category"
                  ? "Kategorie"
                  : type === "video"
                  ? "Video"
                  : type === "article"
                  ? "Artikel"
                  : type === "quiz"
                  ? "Quiz"
                  : "Wiedergabeliste",
            })}
          </span>
        </div>

        <div className="absolute top-2 right-2">
          <ContractBadge contract={item.contract} />
        </div>

        {(type === "video" || item.duration) && (
          <div className="absolute bottom-2 left-2 flex items-center space-x-2">
            {type === "video" && item.language && (
              <LanguageBadge language={item.language} />
            )}
            {item.duration && (
              <div className="bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded flex items-center">
                <Clock className="w-3 h-3 mr-1" />
                {formatDuration(item.duration)}
              </div>
            )}
          </div>
        )}

        {type === "playlist" && (
          <div className="absolute bottom-2 left-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded flex items-center">
            <List className="w-3 h-3 mr-1" />
            {item.videoCount || 0}
          </div>
        )}

        {canAccess && type === "video" && (
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity flex items-center justify-center">
            <Play className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        )}
      </div>

      <div className="p-4">
        <h3 className="text-base font-semibold text-[#002855] mb-1">
          {t(item.title)}
        </h3>
        {item.description && (
          <p className="text-sm text-gray-600 line-clamp-2">
            {t(item.description)}
          </p>
        )}
      </div>
    </Link>
  );
};

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const query = searchParams.get("q") || "";
  const typeFilter = searchParams.get("type") || "all";

  useEffect(() => {
    if (query.trim().length >= 2) {
      performSearch(query, typeFilter);
    } else {
      setSearchResults(null);
    }
  }, [query, language, typeFilter]);

  const performSearch = async (searchTerm, type = "all") => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.search(searchTerm, language, type);
      if (response.success) {
        // Ensure articles and quizzes arrays exist
        const resultsWithAllTypes = {
          ...response.data,
          articles: response.data.articles || [],
          quizzes: response.data.quizzes || []
        };
        setSearchResults(resultsWithAllTypes);
      } else {
        setError("Search failed");
      }
    } catch (error) {
      console.error("Search error:", error);
      setError("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (newType) => {
    const params = new URLSearchParams(searchParams);
    if (newType === "all") {
      params.delete("type");
    } else {
      params.set("type", newType);
    }
    setSearchParams(params);
  };

  const getTotalResults = () => {
    if (!searchResults) return 0;
    return (
      searchResults.categories.length +
      searchResults.videos.length +
      searchResults.playlists.length +
      (searchResults.quizzes?.length || 0) +
      (searchResults.articles?.length || 0)
    );
  };

  const getTypeCount = (type) => {
    if (!searchResults) return 0;
    if (type === 'all') return getTotalResults();
    return searchResults[type]?.length || 0;
  };

  if (loading) return <LoadingSpinner />;

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Link
          to="/"
          className="flex items-center text-[#00A3E0] hover:text-[#002855] mb-4 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {t({ en: "Back to Home", de: "Zurück zur Startseite" })}
        </Link>

        <div className="flex items-center space-x-3 mb-4">
          <Search className="w-6 h-6 text-[#002855]" />
          <h1 className="text-3xl font-bold text-[#002855]">
            {t({ en: "Search Results", de: "Suchergebnisse" })}
          </h1>
        </div>

        {query && (
          <p className="text-lg text-gray-600">
            {searchResults
              ? t({
                  en: `${getTotalResults()} results for "${query}"`,
                  de: `${getTotalResults()} Ergebnisse für "${query}"`,
                })
              : t({
                  en: `Searching for "${query}"...`,
                  de: `Suche nach "${query}"...`,
                })}
          </p>
        )}
      </div>

      {/* Tabbed Navigation */}
      {searchResults && getTotalResults() > 0 && (
        <div className="mb-8 border-b border-gray-200">
          <div className="flex space-x-1 overflow-x-auto">
            {[
              { id: 'all', label: t({ en: 'All', de: 'Alle' }), icon: <LayoutGrid className="w-4 h-4" /> },
              { id: 'videos', label: t({ en: 'Videos', de: 'Videos' }), icon: <Play className="w-4 h-4" /> },
              { id: 'playlists', label: t({ en: 'Playlists', de: 'Wiedergabelisten' }), icon: <List className="w-4 h-4" /> },
              { id: 'quizzes', label: t({ en: 'Quizzes', de: 'Quiz' }), icon: <Trophy className="w-4 h-4" /> },
              { id: 'articles', label: t({ en: 'Articles', de: 'Artikel' }), icon: <Newspaper className="w-4 h-4" /> },
              { id: 'categories', label: t({ en: 'Categories', de: 'Kategorien' }), icon: <Folder className="w-4 h-4" /> }
            ]
              .map(tab => ({
                ...tab,
                count: getTypeCount(tab.id)
              }))
              .filter(tab => {
                // Hide tabs with zero results, except 'all' and currently active tab
                if (tab.id === 'all') return true;
                if (tab.id === typeFilter) return true;
                return tab.count > 0;
              })
              .sort((a, b) => {
                // Always keep 'all' first
                if (a.id === 'all') return -1;
                if (b.id === 'all') return 1;
                // Sort by count descending
                return b.count - a.count;
              })
              .map(tab => {
                const isActive = typeFilter === tab.id;

                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                      isActive
                        ? 'border-[#00A3E0] text-[#00A3E0]'
                        : 'border-transparent text-gray-500 hover:text-[#002855] hover:border-gray-300'
                    }`}
                  >
                    {tab.icon}
                    <span>{tab.label}</span>
                    {/* Don't show count for 'All' when a filter is active */}
                    {!(tab.id === 'all' && typeFilter !== 'all') && (
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        isActive
                          ? 'bg-[#00A3E0] text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {error && (
        <div className="text-center text-[#DA291C] py-8">
          <p>{error}</p>
        </div>
      )}

      {!query.trim() && (
        <div className="text-center py-12">
          <Search className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl text-gray-500 mb-2">
            {t({ en: "Enter a search term", de: "Suchbegriff eingeben" })}
          </h2>
          <p className="text-gray-400">
            {t({
              en: "Search for videos, playlists, categories, and articles",
              de: "Suche nach Videos, Wiedergabelisten, Kategorien und Artikeln",
            })}
          </p>
        </div>
      )}

      {searchResults && getTotalResults() === 0 && query.trim().length >= 2 && (
        <div className="text-center py-12">
          <Search className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl text-gray-500 mb-2">
            {t({ en: "No results found", de: "Keine Ergebnisse gefunden" })}
          </h2>
          <p className="text-gray-400">
            {t({
              en: `Try different keywords or check your spelling`,
              de: "Versuche andere Suchbegriffe oder überprüfe die Rechtschreibung",
            })}
          </p>
        </div>
      )}

      {searchResults && getTotalResults() > 0 && (
        <div className="space-y-8">
          {/* Categories */}
          {(typeFilter === 'all' || typeFilter === 'categories') && searchResults.categories.length > 0 && (
            <section>
              <h2 className="text-2xl font-semibold text-[#002855] mb-4">
                {t({ en: "Categories", de: "Kategorien" })} (
                {searchResults.categories.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {searchResults.categories.map((category) => (
                  <SearchResultCard
                    key={`category-${category.category_id}`}
                    item={category}
                    type="category"
                  />
                ))}
              </div>
            </section>
          )}

          {/* Playlists */}
          {(typeFilter === 'all' || typeFilter === 'playlists') && searchResults.playlists.length > 0 && (
            <section>
              <h2 className="text-2xl font-semibold text-[#002855] mb-4">
                {t({ en: "Playlists", de: "Wiedergabelisten" })} (
                {searchResults.playlists.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {searchResults.playlists.map((playlist) => (
                  <SearchResultCard
                    key={`playlist-${playlist.playlist_id}`}
                    item={playlist}
                    type="playlist"
                  />
                ))}
              </div>
            </section>
          )}

          {/* Videos */}
          {(typeFilter === 'all' || typeFilter === 'videos') && searchResults.videos.length > 0 && (
            <section>
              <h2 className="text-2xl font-semibold text-[#002855] mb-4">
                {t({ en: "Videos", de: "Videos" })} (
                {searchResults.videos.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {searchResults.videos.map((video) => (
                  <SearchResultCard
                    key={`video-${video.video_id}`}
                    item={video}
                    type="video"
                  />
                ))}
              </div>
            </section>
          )}

          {/* Quizzes */}
          {(typeFilter === 'all' || typeFilter === 'quizzes') && searchResults.quizzes?.length > 0 && (
            <section>
              <h2 className="text-2xl font-semibold text-[#002855] mb-4">
                {t({ en: "Quizzes", de: "Quiz" })} (
                {searchResults.quizzes.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {searchResults.quizzes.map((quiz) => (
                  <SearchResultCard
                    key={`quiz-${quiz.quiz_id}`}
                    item={quiz}
                    type="quiz"
                  />
                ))}
              </div>
            </section>
          )}

          {/* Articles */}
          {(typeFilter === 'all' || typeFilter === 'articles') && searchResults.articles?.length > 0 && (
            <section>
              <h2 className="text-2xl font-semibold text-[#002855] mb-4">
                {t({ en: "Articles", de: "Artikel" })} (
                {searchResults.articles.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {searchResults.articles.map((article) => (
                  <SearchResultCard
                    key={`article-${article.id}`}
                    item={article}
                    type="article"
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </main>
  );
};

export default SearchPage;
